import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { accountsAPI, amazonSupportAPI, poaSubmissionsAPI } from '@/lib/api';
import { useProfile } from '@/lib/profile-context';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const EMPTY_STEPS = {
  root_cause: '',
  corrective_actions: '',
  preventative_measures: '',
  supporting_evidence: '',
  closing_commitment: '',
};

const EMPTY_PARSED = {
  store_name: '',
  merchant_token: '',
  marketplace: '',
  violation_type: '',
  policy_cited: '',
  notice_date: '',
  deactivation_date: '',
  affected_asins: [],
  affected_order_ids: [],
  amazon_requested_items: [],
};

const FAMILY_LABELS = {
  1: 'Performance metrics',
  2: 'Product and listing',
  3: 'Account level / Section 3',
  4: 'Verification and compliance',
};

const STEP_LABELS = ['Connect', 'Notice', 'Details', 'Answers', 'Documents', 'Review'];

const ANSWER_META = [
  {
    key: 'root_cause',
    title: 'What caused this violation?',
    sub: 'Amazon calls this the root cause. Investigators want the specific operational failure inside your business — a process that broke, a check that was missing — not blame or apologies.',
    ph: 'e.g. Our supplier changed the packaging in March and we had no inspection step to catch it…',
    good: '“Our supplier switched packaging in March without notifying us. We had no incoming-inspection step, so 240 units shipped without the required suffocation warning label.” — specific, dated, owns the failure.',
    weak: '“Amazon made a mistake.” · “A competitor reported us unfairly.” · “We didn’t know about this rule.” — deflection and ignorance are the two fastest paths to rejection.',
    tips: 'Name dates, SKUs, and quantities. One clear failure beats a list of maybes. Write in first person plural (“we”).',
    sug: 'Between March 3–18, 2026, we shipped 240 units of the affected SKU sourced from our supplier. The supplier changed packaging without notifying us. Our receiving process had no incoming-inspection checkpoint, so the change was not detected before units were sent to FBA.',
  },
  {
    key: 'corrective_actions',
    title: 'What have you already done to fix it?',
    sub: 'Corrective actions — concrete steps you have already completed, written in past tense. Actions still “in progress” belong in preventive measures, not here.',
    ph: 'e.g. We created a removal order for all affected units on July 22…',
    good: '“We created a removal order for all 214 remaining units on July 22 (RMV-8834721), re-labeled recovered inventory, and audited all 34 active listings.” — completed, verifiable, with IDs.',
    weak: '“We will remove the inventory soon.” · “We plan to look into it.” — future tense here reads as nothing done yet.',
    tips: 'Past tense only. Include order IDs, case IDs, and dates Amazon can verify. Quantify everything.',
    sug: 'On the notice date we created a removal order for remaining affected units. We re-labeled recovered inventory with compliant labeling, audited active listings for equivalent requirements, and notified the supplier in writing.',
  },
  {
    key: 'preventative_measures',
    title: 'How will you prevent this from happening again?',
    sub: 'Preventive measures — the systems, checks, and thresholds now in place so the same failure is impossible, not just unlikely. This section carries the most weight with investigators.',
    ph: 'e.g. Every inbound lot is now photographed and checked against a compliance checklist…',
    good: '“Every inbound lot is photographed and checked against a compliance checklist before FBA shipment. Supplier agreements now require 30-day notice of packaging changes.” — systemic, with an owner.',
    weak: '“We will be more careful.” · “This won’t happen again.” — promises without a mechanism are auto-rejected.',
    tips: 'Describe a system, not effort. Name who owns each check and how often it runs. SOPs and audits score well.',
    sug: 'We implemented a mandatory incoming-inspection SOP: every inbound lot is photographed and checked against a compliance checklist before any FBA shipment. Supplier agreements now require written notice of packaging changes. Operations owns a quarterly compliance audit of active listings.',
  },
];

const DEFAULT_DOCS = [
  { name: 'Supplier invoice for affected SKU', req: 'Required', why: 'Proves sourcing chain — investigators verify supplier name matches your root cause.' },
  { name: 'Removal order confirmation', req: 'Required', why: 'Verifies the corrective action you described.' },
  { name: 'Photos of corrected labeling / listing', req: 'Recommended', why: 'Shows compliant inventory or corrected listing content.' },
  { name: 'Incoming-inspection / compliance SOP', req: 'Recommended', why: 'Documents the preventive system — SOPs significantly raise approval odds.' },
];

const EMPTY_COACH = { strong: [], weak: [], tips: [], loading: false, loaded: false, source: 'fallback' };

const DEFAULT_DOC_REQUIREMENTS = DEFAULT_DOCS.map((d) => ({
  name: d.name,
  requirement: d.req === 'Required' ? 'required' : d.req === 'Recommended' ? 'recommended' : 'optional',
  why: d.why,
}));

function cleanNoticePlainText(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let text = raw;
  if (/<[a-z][\s\S]*>/i.test(text)) {
    if (typeof DOMParser !== 'undefined') {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      doc.querySelectorAll('style, script, noscript, head').forEach((el) => el.remove());
      text = doc.body?.textContent || text;
    }
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');
    text = text.replace(/<[^>]+>/g, ' ');
  }
  text = text.replace(/\s+/g, ' ').trim();
  if (/^[\s.#@{]|font-family:/i.test(text)) {
    const match = text.match(
      /(Hello [^,]+,|Your listings have been|We have deactivated|Your Amazon|Your disbursements|Dear seller|Important notice|Under the US INFORM)/i,
    );
    if (match?.index > 0) text = text.slice(match.index).trim();
  }
  return text;
}

function noticeFromNotification(n) {
  const subject = cleanNoticePlainText(n.detail_subject || n.subject || '');
  const body = cleanNoticePlainText(n.message || '');
  return [subject, body].filter(Boolean).join('\n\n');
}

function noticePreviewText(n, max = 180) {
  const preview = cleanNoticePlainText(n.message || n.detail_subject || n.subject || '');
  return preview.length > max ? `${preview.slice(0, max - 1)}…` : preview;
}

function parsedFromApi(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_PARSED };
  const noticeDate = raw.notice_date || '';
  const deactivationDate = raw.deactivation_date || noticeDate || '';
  return {
    ...EMPTY_PARSED,
    ...raw,
    store_name: raw.store_name || '',
    merchant_token: raw.merchant_token || '',
    marketplace: raw.marketplace || '',
    violation_type: raw.violation_type || '',
    policy_cited: raw.policy_cited || '',
    notice_date: noticeDate || deactivationDate,
    deactivation_date: deactivationDate,
    affected_asins: Array.isArray(raw.affected_asins) ? raw.affected_asins : [],
    affected_order_ids: Array.isArray(raw.affected_order_ids) ? raw.affected_order_ids : [],
    amazon_requested_items: Array.isArray(raw.amazon_requested_items) ? raw.amazon_requested_items : [],
  };
}

function wordCount(text) {
  const t = (text || '').trim();
  return t ? t.split(/\s+/).length : 0;
}

function formatCoachBullets(items) {
  if (!Array.isArray(items) || !items.length) return null;
  return items;
}

function CoachPanelBody({ items, fallback, loading }) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-2 text-[#93a1b5]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Tailoring to your notice…
      </span>
    );
  }
  const list = formatCoachBullets(items);
  if (list) {
    return (
      <ul className="m-0 list-disc space-y-1.5 pl-4">
        {list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  return fallback;
}

function WizardBusyOverlay({ title, subtitle }) {
  if (!title || typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{ background: 'rgba(6, 12, 20, 0.72)', backdropFilter: 'blur(6px)' }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="w-full max-w-[420px] rounded-2xl border border-[rgba(148,163,184,.2)] bg-[#101724] px-7 py-8 text-center shadow-[0_24px_80px_rgba(0,0,0,.55)]">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(63,214,204,.35)] bg-[rgba(63,214,204,.1)]">
          <Loader2 className="h-6 w-6 animate-spin text-[#3fd6cc]" />
        </div>
        <div className="text-[16px] font-semibold text-[#e7edf6]">{title}</div>
        {subtitle && (
          <p className="mt-2 text-[13px] leading-relaxed text-[#93a1b5]">{subtitle}</p>
        )}
      </div>
    </div>,
    document.body,
  );
}

const REVIEW_SECTION_LABELS = [
  'Notification',
  'Account details',
  'Root cause',
  'Corrective actions',
  'Preventive measures',
  'Documents',
];

function ReviewSkeletonRow({ label }) {
  return (
    <div className="flex items-start gap-3.5 rounded-xl border border-[rgba(148,163,184,.14)] bg-[#0d1420] px-[18px] py-4">
      <div
        className="mt-0.5 flex-none rounded-full px-2.5 py-0.5 text-[11px] font-bold"
        style={{
          background: 'rgba(148,163,184,.08)',
          border: '1px solid rgba(148,163,184,.2)',
          color: '#93a1b5',
        }}
      >
        <span className="inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking…
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold">{label}</div>
        <div className="mt-1 text-[12.5px] leading-relaxed text-[#5b6779]">
          Verifying against Protego POA rules…
        </div>
      </div>
    </div>
  );
}

function docRequirementLabel(req) {
  if (req === 'required') return 'Required';
  if (req === 'optional') return 'Optional';
  return 'Recommended';
}

function classifyNotice(n) {
  const s = `${n.subject || ''} ${n.detail_subject || ''} ${n.message || ''}`.toLowerCase();
  if (/deactivat|section 3|account at risk|selling privileges/.test(s)) {
    return { group: 'Account deactivation', sev: 'Critical', color: '#f87171' };
  }
  if (/inform consumers|certif(y|ication)/.test(s)) {
    return { group: 'INFORM Consumers Act', sev: 'High', color: '#fbbf24' };
  }
  if (/restricted|listing|policy|safety|asin|sku|counterfeit|ip complaint/.test(s)) {
    return { group: 'Listing policy violations', sev: 'High', color: '#93a1b5' };
  }
  return { group: 'Other performance notices', sev: 'Info', color: '#93a1b5' };
}

function sevStyles(sev) {
  if (sev === 'Critical') return { bg: 'rgba(248,113,113,.12)', border: 'rgba(248,113,113,.35)', color: '#fca5a5' };
  if (sev === 'High') return { bg: 'rgba(251,191,36,.1)', border: 'rgba(251,191,36,.3)', color: '#fcd34d' };
  return { bg: 'rgba(148,163,184,.1)', border: 'rgba(148,163,184,.25)', color: '#93a1b5' };
}

function formatNoticeDate(n) {
  const raw = n.detail_date || n.date || n.created_at;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function AnimatedCollapse({ open, children, durationMs = 180, className = '' }) {
  return (
    <div
      className={`grid overflow-hidden transition-all ease-out ${className}`}
      style={{
        gridTemplateRows: open ? '1fr' : '0fr',
        opacity: open ? 1 : 0,
        transitionDuration: `${durationMs}ms`,
      }}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

export default function PoaWizardPage() {
  const { accounts, accountId, switchAccount, loadAccounts, currentAccount } = useProfile();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answerQ, setAnswerQ] = useState(0);
  const [submission, setSubmission] = useState(null);
  const [noticeText, setNoticeText] = useState('');
  const [steps, setSteps] = useState(EMPTY_STEPS);
  const [parsed, setParsed] = useState(EMPTY_PARSED);
  const [family, setFamily] = useState('3');
  const [routingNote, setRoutingNote] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [prefillHint, setPrefillHint] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [showSug, setShowSug] = useState([false, false, false]);
  const [attachedDocs, setAttachedDocs] = useState([false, false, false, false]);
  const [docFiles, setDocFiles] = useState(['', '', '', '']);
  const [chat, setChat] = useState([
    { who: 'ai', text: 'I can review your answer against what Amazon investigators look for. Ask me anything, or paste a sentence you are unsure about.' },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [coaching, setCoaching] = useState([EMPTY_COACH, EMPTY_COACH, EMPTY_COACH]);
  const [aiDrafts, setAiDrafts] = useState(['', '', '']);
  const [draftLoading, setDraftLoading] = useState(false);
  const [docRequirements, setDocRequirements] = useState(DEFAULT_DOC_REQUIREMENTS);
  const [docsLoading, setDocsLoading] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);
  const [canGenerate, setCanGenerate] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewLoaded, setReviewLoaded] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [allowResubmit, setAllowResubmit] = useState(false);
  const [generatedPoa, setGeneratedPoa] = useState(null);
  const [generatingPoa, setGeneratingPoa] = useState(false);
  const coachingRef = useRef([EMPTY_COACH, EMPTY_COACH, EMPTY_COACH]);
  const prefetchAbortRef = useRef(0);
  const reviewValidationRef = useRef({ key: '', loaded: false });
  const generatedPoaKeyRef = useRef('');

  const readOnly = submission?.status === 'submitted' && !allowResubmit;
  const effectiveAccountId = submission?.account_id || accountId || accounts?.[0]?.id || null;
  const selectedAccount = accounts?.find((a) => String(a.id) === String(effectiveAccountId)) || currentAccount;

  const loadSubmission = useCallback(async () => {
    setLoading(true);
    try {
      const res = await poaSubmissionsAPI.me();
      const sub = res.data?.submission || null;
      setAllowResubmit(Boolean(res.data?.allow_resubmit));
      setSubmission(sub);
      if (sub) {
        setNoticeText(sub.notice_text || '');
        setSteps({ ...EMPTY_STEPS, ...(sub.steps || {}) });
        setSelectedIds(Array.isArray(sub.selected_notification_ids) ? sub.selected_notification_ids : []);
        setParsed((prev) => {
          const fromSub = parsedFromApi(sub.parsed_fields);
          return {
            ...fromSub,
            store_name: fromSub.store_name || prev.store_name || '',
            merchant_token: fromSub.merchant_token || prev.merchant_token || '',
            marketplace: fromSub.marketplace || prev.marketplace || '',
          };
        });
        if (sub.violation_family) setFamily(String(sub.violation_family));
        if (sub.status === 'submitted') setStepIndex(6);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load POA submission');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPrefill = useCallback(async (acctId) => {
    if (!acctId) {
      setNotifications([]);
      setPrefillHint('Connect a Seller Central account first.');
      return;
    }
    try {
      const res = await poaSubmissionsAPI.prefill(acctId);
      const list = res.data?.performance_notifications || [];
      setNotifications(list);
      setPrefillHint(res.data?.hint || null);
      const ctx = res.data?.account_context;
      if (ctx && !readOnly) {
        setParsed((prev) => ({
          ...prev,
          store_name: prev.store_name || ctx.store_name || '',
          merchant_token: prev.merchant_token || ctx.merchant_token || '',
          marketplace: prev.marketplace || ctx.marketplace || '',
        }));
      }
      const groups = {};
      list.forEach((n) => {
        const { group } = classifyNotice(n);
        if (!(group in groups)) groups[group] = group === 'Account deactivation';
      });
      setOpenGroups((prev) => ({ ...groups, ...prev }));
    } catch (err) {
      setPrefillHint(err.response?.data?.message || 'Could not load notices yet.');
    }
  }, [readOnly]);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  useEffect(() => {
    if (effectiveAccountId) loadPrefill(effectiveAccountId);
  }, [effectiveAccountId, loadPrefill]);

  const buildAiPayload = useCallback((section, extra = {}) => ({
    notice_text: noticeText,
    parsed_fields: {
      ...parsed,
      affected_asins: parsed.affected_asins,
      affected_order_ids: parsed.affected_order_ids,
      amazon_requested_items: parsed.amazon_requested_items,
    },
    violation_family: Number(family) || 1,
    section,
    ...extra,
  }), [noticeText, parsed, family]);

  const coachingContextKey = useMemo(() => {
    if (!noticeText.trim()) return '';
    return [
      noticeText.trim(),
      family,
      parsed.violation_type || '',
      parsed.policy_cited || '',
      (parsed.affected_asins || []).join(','),
    ].join('|');
  }, [noticeText, family, parsed.violation_type, parsed.policy_cited, parsed.affected_asins]);

  const reviewValidationKey = useMemo(() => [
    coachingContextKey,
    steps.root_cause || '',
    steps.corrective_actions || '',
    steps.preventative_measures || '',
    steps.supporting_evidence || '',
    attachedDocs.filter(Boolean).length,
    docRequirements.length,
  ].join('|'), [coachingContextKey, steps, attachedDocs, docRequirements.length]);

  useEffect(() => {
    coachingRef.current = coaching;
  }, [coaching]);

  useEffect(() => {
    if (!reviewValidationKey) return;
    reviewValidationRef.current = { key: '', loaded: false };
    setReviewLoaded(false);
    setReviewItems([]);
    setCanGenerate(false);
  }, [reviewValidationKey]);

  useEffect(() => {
    if (!coachingContextKey) return;
    prefetchAbortRef.current += 1;
    setCoaching([EMPTY_COACH, EMPTY_COACH, EMPTY_COACH]);
  }, [coachingContextKey]);

  const loadCoaching = useCallback(async (qIndex, { force = false } = {}) => {
    if (!noticeText.trim() || Number(family) === 4) return false;
    const section = ANSWER_META[qIndex]?.key;
    if (!section) return false;
    if (!force && coachingRef.current[qIndex]?.loaded) return true;

    setCoaching((prev) => {
      if (!force && prev[qIndex]?.loaded) return prev;
      const next = [...prev];
      next[qIndex] = { ...next[qIndex], loading: true };
      return next;
    });

    try {
      const res = await poaSubmissionsAPI.aiCoachSection(buildAiPayload(section, {
        current_answer: steps[section] || '',
      }));
      setCoaching((prev) => {
        const next = [...prev];
        next[qIndex] = {
          strong: res.data?.strong || [],
          weak: res.data?.weak || [],
          tips: res.data?.tips || [],
          loading: false,
          loaded: true,
          source: res.data?.source || 'claude',
        };
        return next;
      });
      return true;
    } catch {
      setCoaching((prev) => {
        const next = [...prev];
        next[qIndex] = { ...EMPTY_COACH, loading: false, loaded: true };
        return next;
      });
      return false;
    }
  }, [noticeText, family, steps, buildAiPayload]);

  const prefetchAllCoaching = useCallback(async () => {
    if (!coachingContextKey || Number(family) === 4) return;
    const runId = prefetchAbortRef.current;
    for (let i = 0; i < ANSWER_META.length; i += 1) {
      if (runId !== prefetchAbortRef.current) return;
      if (coachingRef.current[i]?.loaded) continue;
      await loadCoaching(i);
    }
  }, [coachingContextKey, family, loadCoaching]);

  const loadDraft = useCallback(async (qIndex, regenerate = false) => {
    const section = ANSWER_META[qIndex]?.key;
    if (!section || !noticeText.trim()) return;
    setDraftLoading(true);
    setShowSug((s) => { const n = [...s]; n[qIndex] = true; return n; });
    try {
      const res = await poaSubmissionsAPI.aiDraftSection(buildAiPayload(section, {
        current_answer: regenerate ? steps[section] || '' : '',
      }));
      const draft = res.data?.draft || '';
      setAiDrafts((prev) => {
        const next = [...prev];
        next[qIndex] = draft;
        return next;
      });
    } catch {
      toast.error('Could not generate AI draft');
      setShowSug((s) => { const n = [...s]; n[qIndex] = false; return n; });
    } finally {
      setDraftLoading(false);
    }
  }, [noticeText, steps, buildAiPayload]);

  const loadDocumentRequirements = useCallback(async () => {
    if (!noticeText.trim()) return;
    setDocsLoading(true);
    try {
      const res = await poaSubmissionsAPI.aiDocumentRequirements(buildAiPayload('root_cause'));
      const docs = res.data?.documents;
      if (Array.isArray(docs) && docs.length) {
        setDocRequirements(docs);
      }
    } catch {
      setDocRequirements(DEFAULT_DOC_REQUIREMENTS);
    } finally {
      setDocsLoading(false);
    }
  }, [noticeText, buildAiPayload]);

  const loadReviewValidation = useCallback(async () => {
    if (!noticeText.trim()) return;
    if (reviewValidationRef.current.key === reviewValidationKey && reviewValidationRef.current.loaded) {
      setReviewLoaded(true);
      return;
    }
    setReviewLoading(true);
    setReviewLoaded(false);
    try {
      const res = await poaSubmissionsAPI.aiValidateReview({
        ...buildAiPayload('root_cause'),
        steps,
        attached_doc_count: attachedDocs.filter(Boolean).length,
        suggested_doc_count: docRequirements.length,
      });
      setReviewItems(res.data?.items || []);
      setCanGenerate(Boolean(res.data?.can_generate));
      reviewValidationRef.current = { key: reviewValidationKey, loaded: true };
      setReviewLoaded(true);
    } catch {
      setReviewItems([]);
      setCanGenerate(false);
      reviewValidationRef.current = { key: reviewValidationKey, loaded: true };
      setReviewLoaded(true);
    } finally {
      setReviewLoading(false);
    }
  }, [noticeText, steps, attachedDocs, docRequirements.length, buildAiPayload, reviewValidationKey]);

  const loadGeneratedPoa = useCallback(async (nextSteps = steps, { force = false } = {}) => {
    if (!noticeText.trim()) return;
    const key = [
      noticeText.slice(0, 200),
      family,
      nextSteps.root_cause || '',
      nextSteps.corrective_actions || '',
      nextSteps.preventative_measures || '',
      nextSteps.supporting_evidence || '',
    ].join('|');
    if (!force && generatedPoaKeyRef.current === key) return;
    if (force) generatedPoaKeyRef.current = '';
    setGeneratingPoa(true);
    try {
      const res = await poaSubmissionsAPI.aiGenerateDocument({
        notice_text: noticeText,
        parsed_fields: parsed,
        violation_family: Number(family),
        steps: nextSteps,
      });
      const doc = res.data || null;
      setGeneratedPoa(doc);
      generatedPoaKeyRef.current = key;
      if (doc?.source === 'claude') {
        if (force) toast.success('POA revamped with Protego AI');
      } else if (doc?.source === 'fallback') {
        toast.message('AI unavailable on server — showing intake text. Check ANTHROPIC_API_KEY on live.');
      }
    } catch (err) {
      generatedPoaKeyRef.current = key;
      setGeneratedPoa({
        title: `Plan of Action: ${parsed.violation_type || FAMILY_LABELS[family] || 'Account reinstatement'}`,
        opening: '',
        sections: [
          { heading: 'ROOT CAUSES', body: nextSteps.root_cause || '—' },
          { heading: 'ACTIONS TAKEN TO RESOLVE THE ISSUES', body: nextSteps.corrective_actions || '—' },
          { heading: 'STEPS TAKEN TO PREVENT VIOLATIONS IN THE FUTURE', body: nextSteps.preventative_measures || '—' },
          { heading: 'SUPPORTING DOCUMENTS', body: nextSteps.supporting_evidence || '—' },
        ],
        closing: '',
        full_text: '',
        source: 'fallback',
      });
      const status = err?.response?.status;
      const timedOut = !err?.response && (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || ''));
      if (timedOut || status === 502 || status === 504) {
        toast.error('AI revamp timed out on live (gateway). Tap “Revamp again” to retry.');
      } else if (status === 404) {
        toast.error('Generate endpoint missing on live — redeploy backend, then Revamp again.');
      } else {
        toast.error(err?.response?.data?.message || 'Could not revamp POA with AI — showing your intake text');
      }
    } finally {
      setGeneratingPoa(false);
    }
  }, [noticeText, family, steps, parsed]);

  const revampPoaAgain = useCallback(() => {
    loadGeneratedPoa(steps, { force: true });
  }, [loadGeneratedPoa, steps]);

  const downloadPoaPdf = useCallback(() => {
    const title = generatedPoa?.title
      || `Plan of Action: ${parsed.violation_type || FAMILY_LABELS[family] || 'Account reinstatement'}`;
    const sections = generatedPoa?.sections?.length
      ? generatedPoa.sections
      : [
        { heading: 'ROOT CAUSES', body: steps.root_cause || '—' },
        { heading: 'ACTIONS TAKEN TO RESOLVE THE ISSUES', body: steps.corrective_actions || '—' },
        { heading: 'STEPS TAKEN TO PREVENT VIOLATIONS IN THE FUTURE', body: steps.preventative_measures || '—' },
        { heading: 'SUPPORTING DOCUMENTS', body: steps.supporting_evidence || '—' },
      ];
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const margin = 48;
    const maxWidth = 516;
    let y = margin;
    const ensureSpace = (needed = 40) => {
      if (y + needed > 744) {
        doc.addPage();
        y = margin;
      }
    };
    const writeWrapped = (text, fontSize, style = 'normal', color = [28, 36, 48]) => {
      doc.setFont('helvetica', style);
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text || '—'), maxWidth);
      lines.forEach((line) => {
        ensureSpace(fontSize + 8);
        doc.text(line, margin, y);
        y += fontSize + 6;
      });
    };
    writeWrapped(
      `PLAN OF ACTION — ${(parsed.store_name || 'STORE').toUpperCase()} · ${parsed.merchant_token || '—'} · ${(parsed.marketplace || 'AMAZON').toUpperCase()}`,
      9,
      'bold',
      [90, 95, 105],
    );
    y += 8;
    writeWrapped(title, 14, 'bold', [19, 26, 36]);
    y += 10;
    if (generatedPoa?.opening) {
      writeWrapped(generatedPoa.opening, 10, 'normal', [57, 66, 78]);
      y += 10;
    }
    sections.forEach((section) => {
      y += 6;
      writeWrapped(section.heading || 'Section', 11, 'bold', [19, 26, 36]);
      y += 2;
      writeWrapped(section.body || '—', 10, 'normal', [57, 66, 78]);
      y += 8;
    });
    if (generatedPoa?.closing) {
      y += 4;
      writeWrapped(generatedPoa.closing, 10, 'normal', [57, 66, 78]);
    }
    const fileSafe = (parsed.store_name || 'POA').replace(/[^\w\-]+/g, '_').slice(0, 40);
    doc.save(`${fileSafe}_Plan_of_Action.pdf`);
    toast.success('PDF downloaded');
  }, [generatedPoa, parsed, family, steps]);

  useEffect(() => {
    // Prefetch strong/weak/tips as soon as Details (post-parse) is ready — before Answers.
    if (stepIndex >= 2 && stepIndex <= 3 && Number(family) !== 4 && coachingContextKey) {
      prefetchAllCoaching();
    }
  }, [stepIndex, family, coachingContextKey, prefetchAllCoaching]);

  useEffect(() => {
    if (stepIndex === 3 && Number(family) !== 4) {
      setChat([{ who: 'ai', text: 'I can review your answer against what Amazon investigators look for. Ask me anything, or paste a sentence you are unsure about.' }]);
    }
  }, [stepIndex, answerQ, family]);

  useEffect(() => {
    if (stepIndex !== 6 || !noticeText.trim()) return;
    loadGeneratedPoa(steps);
  }, [stepIndex, noticeText, steps, loadGeneratedPoa]);

  useEffect(() => {
    if (stepIndex === 4) loadDocumentRequirements();
  }, [stepIndex, loadDocumentRequirements]);

  useEffect(() => {
    if (stepIndex >= 4 && stepIndex <= 5 && reviewValidationKey) {
      loadReviewValidation();
    }
  }, [stepIndex, reviewValidationKey, loadReviewValidation]);

  useEffect(() => {
    const len = docRequirements.length;
    setAttachedDocs((prev) => {
      const next = [...prev];
      while (next.length < len) next.push(false);
      return next.slice(0, len);
    });
    setDocFiles((prev) => {
      const next = [...prev];
      while (next.length < len) next.push('');
      return next.slice(0, len);
    });
  }, [docRequirements.length]);

  const noticeGroups = useMemo(() => {
    const map = new Map();
    notifications.forEach((n) => {
      const meta = classifyNotice(n);
      if (!map.has(meta.group)) map.set(meta.group, { ...meta, items: [] });
      map.get(meta.group).items.push(n);
    });
    return Array.from(map.values());
  }, [notifications]);

  const persistDraft = async (patch = {}, { quiet = false } = {}) => {
    if (readOnly) return submission;
    if (!quiet) setSaving(true);
    try {
      const payload = {
        account_id: effectiveAccountId ? Number(effectiveAccountId) : null,
        notice_text: noticeText,
        steps,
        selected_notification_ids: selectedIds,
        parsed_fields: {
          ...parsed,
          affected_asins: parsed.affected_asins,
          affected_order_ids: parsed.affected_order_ids,
          amazon_requested_items: parsed.amazon_requested_items,
        },
        violation_family: Number(family) || null,
        family_confirmed: true,
        ...patch,
      };
      const res = await poaSubmissionsAPI.saveDraft(payload);
      setSubmission(res.data?.submission || null);
      return res.data?.submission;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save draft');
      return null;
    } finally {
      if (!quiet) setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await accountsAPI.startSpConnect({
        account_name: 'POA reinstatement account',
        marketplace_id: 'ATVPDKIKX0DER',
      });
      const data = res.data || res;
      if (data.redirect_url) {
        await persistDraft();
        window.location.href = data.redirect_url;
        return;
      }
      toast.error('No redirect URL from Amazon connect');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start Amazon connection');
    } finally {
      setConnecting(false);
    }
  };

  const handleRefreshAccounts = async () => {
    try {
      await loadAccounts?.();
      toast.success('Accounts refreshed');
    } catch {
      toast.error('Could not refresh accounts');
    }
  };

  const handleTriggerScrape = async () => {
    if (!effectiveAccountId) {
      toast.error('Connect an account first');
      return;
    }
    setScraping(true);
    try {
      const res = await amazonSupportAPI.triggerScrape(effectiveAccountId);
      toast.success(res.data?.message || 'Scrape started — pull latest in a few seconds.');
      setTimeout(() => loadPrefill(effectiveAccountId), 5000);
      setTimeout(() => loadPrefill(effectiveAccountId), 15000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start scrape (support access may be required)');
    } finally {
      setScraping(false);
    }
  };

  const selectNotification = (id) => {
    if (readOnly) return;
    const num = Number(id);
    setSelectedIds([num]);
    const n = notifications.find((x) => Number(x.id) === num);
    if (n) setNoticeText(noticeFromNotification(n));
  };

  const runParse = async () => {
    if (!noticeText.trim()) {
      toast.error('Paste the full Amazon notice first (or pull & select a scraped notification)');
      return false;
    }
    setParsing(true);
    try {
      const res = await poaSubmissionsAPI.parse({
        notice_text: noticeText,
        account_id: effectiveAccountId ? Number(effectiveAccountId) : null,
        selected_notification_ids: selectedIds,
        persist: true,
      });
      const fields = parsedFromApi(res.data?.parsed_fields);
      setParsed(fields);
      if (res.data?.routing?.family) {
        setFamily(String(res.data.routing.family));
        setRoutingNote(
          `${res.data.routing.label} (${res.data.routing.confidence}): ${res.data.routing.reason}`,
        );
      }
      if (res.data?.submission) setSubmission(res.data.submission);
      // Kick off coaching generation while user reviews Details fields.
      prefetchAbortRef.current += 1;
      setCoaching([EMPTY_COACH, EMPTY_COACH, EMPTY_COACH]);
      coachingRef.current = [EMPTY_COACH, EMPTY_COACH, EMPTY_COACH];
      if (res.data?.manual_entry_required) {
        toast.message(res.data?.message || 'Fill fields manually — auto-parse found little usable text');
      } else {
        toast.success(res.data?.message || 'Notice parsed — confirm fields below');
      }
      return true;
    } catch (err) {
      const data = err.response?.data;
      if (data?.parsed_fields) setParsed(parsedFromApi(data.parsed_fields));
      if (data?.routing?.family) {
        setFamily(String(data.routing.family));
        setRoutingNote(data.routing.reason);
      }
      toast.error(data?.message || 'Parse failed — you can fill fields manually');
      return true;
    } finally {
      setParsing(false);
    }
  };

  const ansFilled = useCallback((i) => Boolean((steps[ANSWER_META[i].key] || '').trim()), [steps]);
  const ansOk = useCallback((i) => wordCount(steps[ANSWER_META[i].key]) >= 40, [steps]);
  const attachedCount = attachedDocs.filter(Boolean).length;

  const canAdvance = useMemo(() => {
    if (stepIndex === 0) return Boolean(accounts?.length || effectiveAccountId);
    if (stepIndex === 1) return Boolean(noticeText.trim());
    if (stepIndex === 2) return Boolean(Number(family) >= 1 && Number(family) <= 4);
    if (stepIndex === 3) {
      if (Number(family) === 4) return Boolean(steps.supporting_evidence?.trim());
      return ansFilled(answerQ);
    }
    if (stepIndex === 4) return true;
    return true;
  }, [stepIndex, accounts, effectiveAccountId, noticeText, family, steps, answerQ, ansFilled]);

  const canSubmitPoa = useMemo(() => {
    if (Number(family) === 4) return Boolean(steps.supporting_evidence?.trim());
    return ANSWER_META.some((_, i) => ansFilled(i)) || Boolean(steps.supporting_evidence?.trim());
  }, [family, steps, ansFilled]);

  const buildEvidenceFromDocs = (prevSteps = steps) => {
    const lines = docRequirements.map((d, i) => {
      if (!attachedDocs[i]) return null;
      return `${d.name}${docFiles[i] ? `: ${docFiles[i]}` : ''}`;
    }).filter(Boolean);
    if (!lines.length) return prevSteps;
    if (prevSteps.supporting_evidence?.trim()) return prevSteps;
    return { ...prevSteps, supporting_evidence: lines.join('\n') };
  };

  const goNext = async () => {
    if (readOnly) {
      setStepIndex((i) => Math.min(6, i + 1));
      return;
    }
    if (stepIndex === 1) {
      const ok = await runParse();
      if (!ok) return;
      setStepIndex(2);
      return;
    }
    if (stepIndex === 3 && Number(family) !== 4 && answerQ < 2) {
      await persistDraft();
      setAnswerQ((q) => q + 1);
      return;
    }
    if (stepIndex === 4) {
      const nextSteps = buildEvidenceFromDocs();
      setSteps(nextSteps);
      const saved = await persistDraft({ steps: nextSteps });
      if (!saved) return;
      setStepIndex(5);
      return;
    }
    if (stepIndex === 5) {
      await handleSubmit();
      return;
    }
    const saved = await persistDraft();
    if (!saved && stepIndex < 5) return;
    setStepIndex((i) => Math.min(5, i + 1));
  };

  const goBack = () => {
    if (stepIndex === 3 && answerQ > 0 && Number(family) !== 4) {
      setAnswerQ((q) => q - 1);
      return;
    }
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const nextSteps = buildEvidenceFromDocs();
      setSteps(nextSteps);
      const res = await poaSubmissionsAPI.submit({
        account_id: effectiveAccountId ? Number(effectiveAccountId) : null,
        notice_text: noticeText,
        steps: nextSteps,
        selected_notification_ids: selectedIds,
        parsed_fields: parsed,
        violation_family: Number(family),
        family_confirmed: true,
      });
      setSubmission(res.data?.submission || null);
      toast.success(res.data?.message || 'Submitted');
      generatedPoaKeyRef.current = '';
      setGeneratedPoa(null);
      setStepIndex(6);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  const setListField = (key, value) => {
    const items = value.split('\n').map((s) => s.trim()).filter(Boolean);
    setParsed((prev) => ({ ...prev, [key]: items }));
  };

  const nextLabel = useMemo(() => {
    if (stepIndex === 5) return '✦ Generate Plan of Action';
    if (stepIndex === 4) return 'Review everything →';
    if (stepIndex === 3 && Number(family) !== 4 && answerQ < 2) return 'Next question →';
    if (stepIndex === 1) return 'Continue →';
    return 'Continue →';
  }, [stepIndex, answerQ, family]);

  const reviewRows = useMemo(() => {
    const nav = {
      Notification: () => setStepIndex(1),
      'Account details': () => setStepIndex(2),
      'Root cause': () => { setStepIndex(3); setAnswerQ(0); },
      'Corrective actions': () => { setStepIndex(3); setAnswerQ(1); },
      'Preventive measures': () => { setStepIndex(3); setAnswerQ(2); },
      Documents: () => setStepIndex(4),
    };
    if (reviewItems.length) {
      return reviewItems.map((r) => ({
        ...r,
        go: nav[r.label] || (() => setStepIndex(2)),
      }));
    }
    if (!reviewLoaded) return [];
    return [
      {
        label: 'Notification',
        summary: noticeText.trim().slice(0, 160) || 'Not selected',
        status: noticeText.trim() ? 'ready' : 'needs_work',
        reasons: [],
        go: nav.Notification,
      },
      {
        label: 'Account details',
        summary: `${parsed.store_name || '—'} · ${parsed.merchant_token || '—'} · Family ${family}`,
        status: (parsed.store_name || parsed.merchant_token) ? 'ready' : 'needs_work',
        reasons: [],
        go: nav['Account details'],
      },
      ...ANSWER_META.map((q, qi) => ({
        label: q.key === 'root_cause' ? 'Root cause' : q.key === 'corrective_actions' ? 'Corrective actions' : 'Preventive measures',
        summary: steps[q.key]?.trim() || 'Not answered yet',
        status: ansOk(qi) ? 'ready' : 'needs_work',
        reasons: [],
        go: nav[q.key === 'root_cause' ? 'Root cause' : q.key === 'corrective_actions' ? 'Corrective actions' : 'Preventive measures'],
      })),
      {
        label: 'Documents',
        summary: `${attachedCount} of ${docRequirements.length} suggested documents attached${steps.supporting_evidence ? ' · notes saved' : ''}`,
        status: (attachedCount > 0 || steps.supporting_evidence?.trim()) ? 'ready' : 'needs_work',
        reasons: [],
        go: nav.Documents,
      },
    ];
  }, [reviewItems, reviewLoaded, noticeText, parsed, family, steps, ansOk, attachedCount, docRequirements.length]);

  const coachingPrefetching = useMemo(
    () => coaching.some((c) => c.loading && !c.loaded),
    [coaching],
  );

  const railMeta = [
    selectedAccount ? 'Account linked' : 'Select account',
    selectedIds.length || noticeText.trim() ? 'Notice selected' : 'Pick a notice',
    `Family ${family} confirmed`,
    Number(family) === 4 ? 'Documents' : '3 questions',
    `${attachedCount} of ${docRequirements.length} attached`,
    'Generate POA',
  ];

  const chatSend = async () => {
    const t = chatInput.trim();
    if (!t || chatLoading) return;
    const section = ANSWER_META[answerQ]?.key;
    if (!section) return;
    const history = chat
      .filter((m) => m.who === 'me' || m.who === 'ai')
      .map((m) => ({
        role: m.who === 'ai' ? 'assistant' : 'user',
        content: m.text,
      }));
    setChat((prev) => [...prev, { who: 'me', text: t }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await poaSubmissionsAPI.aiChat(buildAiPayload(section, {
        message: t,
        history,
        current_answer: steps[section] || '',
      }));
      setChat((prev) => [...prev, { who: 'ai', text: res.data?.reply || 'No response.' }]);
    } catch {
      setChat((prev) => [...prev, { who: 'ai', text: 'Sorry, Protego AI is unavailable right now.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-[#93a1b5]">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading your POA intake…
      </div>
    );
  }

  const busyOverlay = (() => {
    if (parsing) {
      return {
        title: 'Parsing your notice with Protego AI…',
        subtitle: 'Extracting violation type, policy, dates, and ASINs. This can take a few seconds.',
      };
    }
    if (saving && stepIndex === 5) {
      return {
        title: 'Submitting your Plan of Action…',
        subtitle: 'Saving your intake, then Claude will revamp it into a polished POA.',
      };
    }
    if (generatingPoa) {
      return {
        title: 'Revamping your Plan of Action…',
        subtitle: 'Claude is polishing your answers into an investigator-ready document.',
      };
    }
    if (saving) {
      return {
        title: 'Saving your progress…',
        subtitle: 'Updating your draft before the next step.',
      };
    }
    if (scraping) {
      return {
        title: 'Pulling latest Amazon notices…',
        subtitle: 'Seller Central scrape started. Notices will refresh shortly.',
      };
    }
    if (connecting) {
      return {
        title: 'Connecting Amazon account…',
        subtitle: 'Redirecting to Seller Central authorization.',
      };
    }
    if (draftLoading) {
      return {
        title: 'Drafting your answer with AI…',
        subtitle: 'Protego is writing a section draft from your notice and ruleset.',
      };
    }
    if (docsLoading) {
      return {
        title: 'Loading document requirements…',
        subtitle: 'Protego AI is listing evidence Amazon expects for this notice.',
      };
    }
    if (reviewLoading) {
      return {
        title: 'Checking answers against Protego rules…',
        subtitle: 'Validating each section before you generate.',
      };
    }
    return null;
  })();

  const card = 'mt-6 rounded-[14px] border border-[rgba(148,163,184,.14)] bg-[#101724] p-7';
  const fieldBox = 'w-full rounded-[9px] border border-[rgba(148,163,184,.18)] bg-[#0d1420] px-3.5 py-2.5 text-[13.5px] font-medium text-[#e7edf6] outline-none focus:border-[rgba(63,214,204,.5)]';
  const outlineBtn = 'rounded-[9px] border border-[rgba(148,163,184,.25)] bg-transparent px-4 py-2.5 text-[13px] font-semibold text-[#e7edf6] transition hover:border-[rgba(63,214,204,.5)] disabled:opacity-50';
  const primaryBtn = 'rounded-[10px] border-none bg-[#3fd6cc] px-6 py-3 text-[13.5px] font-bold text-[#06121a] transition hover:opacity-90 disabled:opacity-50';

  return (
    <div className="min-h-full text-[#e7edf6]" data-testid="poa-wizard" style={{ background: 'radial-gradient(1200px 500px at 70% -10%,rgba(63,214,204,.05),transparent)' }}>
      <WizardBusyOverlay title={busyOverlay?.title} subtitle={busyOverlay?.subtitle} />
      <div className="mx-auto max-w-[1180px] px-4 py-8 md:px-10 md:pb-20">
        <div className="flex flex-wrap items-start gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight">Plan of Action</h1>
            <div className="mt-1.5 text-[13px] text-[#93a1b5]">
              Order <span className="font-semibold text-[#e7edf6]">#{submission?.id ? `POA-${submission.id}` : 'POA-draft'}</span>
              {' · '}Guided intake, then we generate your customized POA.
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 rounded-full border border-[rgba(52,211,153,.35)] bg-[rgba(52,211,153,.08)] px-3 py-1.5 text-xs text-[#6ee7b7]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#34d399]" />
            {readOnly ? 'Submitted' : allowResubmit && submission?.status === 'submitted' ? 'Submitted · edit & resubmit enabled' : 'Payment confirmed'}
          </div>
        </div>

        <div className="mt-7 flex gap-8 items-start">
          {/* Sticky rail */}
          <aside className="sticky top-2 hidden w-[196px] flex-none pt-1 lg:block">
            {STEP_LABELS.map((label, i) => {
              const done = stepIndex > i || (i === 5 && stepIndex === 6);
              const active = stepIndex === i || (i === 5 && stepIndex === 6);
              return (
                <div key={label} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => (readOnly || done || i <= stepIndex ? setStepIndex(i) : null)}
                      className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full text-[11px] font-bold"
                      style={{
                        background: done ? 'rgba(52,211,153,.15)' : active ? '#3fd6cc' : 'transparent',
                        border: done || active ? 'none' : '2px solid rgba(148,163,184,.3)',
                        color: done ? '#34d399' : active ? '#06121a' : '#5b6779',
                      }}
                    >
                      {done ? '✓' : String(i + 1)}
                    </button>
                    {i < 5 && (
                      <span
                        className="my-1 min-h-[14px] w-0.5 flex-1 rounded-sm"
                        style={{ background: done ? 'rgba(52,211,153,.35)' : 'rgba(148,163,184,.15)' }}
                      />
                    )}
                  </div>
                  <div className="min-w-0 pb-4">
                    <button
                      type="button"
                      onClick={() => (readOnly || done || i <= stepIndex ? setStepIndex(i) : null)}
                      className="text-left text-[13px] font-semibold leading-[22px]"
                      style={{ color: active ? '#8ceee6' : done ? '#e7edf6' : '#5b6779' }}
                    >
                      {label}
                    </button>
                    <div className="mt-0.5 text-[11px] text-[#5b6779]">{railMeta[i]}</div>
                    {i === 3 && stepIndex === 3 && Number(family) !== 4 && (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {ANSWER_META.map((q, qi) => {
                          const coach = coaching[qi];
                          const pending = !coach.loaded && coach.loading;
                          const prefix = coach.loaded
                            ? (ansOk(qi) ? '✓ ' : '· ')
                            : pending
                              ? '… '
                              : '· ';
                          return (
                          <button
                            key={q.key}
                            type="button"
                            onClick={() => setAnswerQ(qi)}
                            className="text-left text-[11.5px] font-semibold"
                            style={{ color: answerQ === qi ? '#8ceee6' : coach.loaded && ansOk(qi) ? '#6ee7b7' : pending ? '#93a1b5' : '#5b6779' }}
                          >
                            {prefix + (qi === 0 ? 'Root cause' : qi === 1 ? 'Corrective actions' : 'Preventive measures')}
                            {pending && answerQ !== qi ? ' (loading)' : ''}
                          </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </aside>

          <div className="min-w-0 flex-1 -mt-1">
            {/* Step 0 — Connect */}
            {stepIndex === 0 && (
              <div className={card}>
                <h2 className="m-0 text-lg font-semibold">Connect your Amazon seller account</h2>
                <p className="mt-2 max-w-[620px] text-[13.5px] leading-relaxed text-[#93a1b5]">
                  We use read-only SP-API access to pull your account context — merchant ID, status, and performance notifications. We can never change anything in your account.
                </p>
                <div className="mt-5 flex flex-col gap-2.5">
                  {(accounts || []).length === 0 && (
                    <div className="rounded-xl border border-[rgba(148,163,184,.14)] bg-[#0d1420] px-4 py-4 text-sm text-[#93a1b5]">
                      No Amazon account linked yet. Connect Seller Central to continue.
                    </div>
                  )}
                  {(accounts || []).map((a) => {
                    const sel = String(effectiveAccountId) === String(a.id);
                    const name = a.accountName || a.name || `Account ${a.id}`;
                    const meta = [a.support_country || a.marketplace || 'US', a.sellerId || parsed.merchant_token || '', a.support_access_status === 'granted' ? 'support access' : null]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <button
                        key={a.id}
                        type="button"
                        disabled={readOnly}
                        onClick={() => {
                          switchAccount?.(String(a.id));
                          // Quiet save — no full-page overlay for account radio clicks.
                          persistDraft({ account_id: Number(a.id) }, { quiet: true });
                        }}
                        className="flex items-center gap-3.5 rounded-xl px-[18px] py-[15px] text-left transition"
                        style={{
                          background: sel ? 'rgba(63,214,204,.06)' : '#0d1420',
                          border: sel ? '1px solid rgba(63,214,204,.55)' : '1px solid rgba(148,163,184,.14)',
                        }}
                      >
                        <span
                          className="box-border h-[18px] w-[18px] flex-none rounded-full"
                          style={{
                            border: sel ? '5px solid #3fd6cc' : '2px solid #5b6779',
                            background: sel ? '#06121a' : 'transparent',
                          }}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{name}</div>
                          <div className="mt-0.5 text-xs text-[#93a1b5]">{meta || 'Connected'}</div>
                        </div>
                        <div
                          className="ml-auto flex-none rounded-full px-2.5 py-1 text-[11.5px] font-semibold"
                          style={{
                            background: sel ? 'rgba(63,214,204,.12)' : 'rgba(52,211,153,.1)',
                            border: sel ? '1px solid rgba(63,214,204,.4)' : '1px solid rgba(52,211,153,.3)',
                            color: sel ? '#8ceee6' : '#6ee7b7',
                          }}
                        >
                          {sel ? 'Selected' : 'Connected'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button type="button" className={outlineBtn} onClick={handleConnect} disabled={readOnly || connecting}>
                    {connecting ? 'Connecting…' : '+ Connect another account'}
                  </button>
                  <button type="button" className="bg-transparent px-4 py-2.5 text-[13px] text-[#93a1b5] hover:text-[#e7edf6]" onClick={handleRefreshAccounts}>
                    ⟳ Refresh
                  </button>
                  <div className="ml-auto text-xs text-[#5b6779]">
                    Notifications auto-pull requires{' '}
                    <Link className="text-[#3fd6cc] hover:underline" to="/accounts">support access</Link>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1 — Notice */}
            {stepIndex === 1 && (
              <div className={card}>
                <div className="flex flex-wrap items-start gap-3">
                  <div>
                    <h2 className="m-0 text-lg font-semibold">Which notification triggered your suspension?</h2>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-[#93a1b5]">
                      {notifications.length
                        ? `We pulled ${notifications.length} performance notifications from Seller Central. Pick the one your appeal responds to.`
                        : 'Pull Seller Central notices when support access is granted, or paste the Amazon email.'}
                    </p>
                  </div>
                  <button type="button" className={`${outlineBtn} ml-auto text-[12.5px]`} onClick={handleTriggerScrape} disabled={readOnly || scraping || !effectiveAccountId}>
                    {scraping ? 'Pulling…' : '⟳ Pull latest notices'}
                  </button>
                </div>
                {prefillHint && (
                  <p className="mt-3 text-xs text-amber-400/90">{prefillHint}</p>
                )}
                <div className="mt-5 flex flex-col gap-2.5">
                  {noticeGroups.map((g) => {
                    const open = !!openGroups[g.group];
                    return (
                      <div key={g.group} className="overflow-hidden rounded-xl border border-[rgba(148,163,184,.14)] bg-[#0d1420]">
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-[rgba(148,163,184,.05)]"
                          onClick={() => setOpenGroups((prev) => ({ ...prev, [g.group]: !open }))}
                        >
                          <span className="h-2 w-2 flex-none rounded-full" style={{ background: g.color }} />
                          <span className="text-[13.5px] font-semibold">{g.group}</span>
                          <span className="rounded-full bg-[rgba(148,163,184,.1)] px-2 py-0.5 text-[11.5px] text-[#5b6779]">
                            {g.items.length} notices
                          </span>
                          <span className="ml-auto text-[11px] text-[#5b6779]">{open ? '▲' : '▼'}</span>
                        </button>
                        <AnimatedCollapse open={open} durationMs={170}>
                          <div className="flex flex-col gap-2 border-t border-[rgba(148,163,184,.1)] p-2.5">
                            {g.items.map((n) => {
                              const id = Number(n.id);
                              const sel = selectedIds.includes(id);
                              const sev = classifyNotice(n).sev;
                              const c = sevStyles(sev);
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  disabled={readOnly}
                                  onClick={() => selectNotification(id)}
                                  className="flex items-start gap-3 rounded-[10px] px-[15px] py-3 text-left"
                                  style={{
                                    background: sel ? 'rgba(63,214,204,.06)' : 'transparent',
                                    border: sel ? '1px solid rgba(63,214,204,.55)' : '1px solid rgba(148,163,184,.1)',
                                  }}
                                >
                                  <span
                                    className="mt-0.5 box-border h-4 w-4 flex-none rounded-full"
                                    style={{ border: sel ? '4.5px solid #3fd6cc' : '2px solid #5b6779' }}
                                  />
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-baseline gap-2">
                                      <div className="text-[13px] font-semibold leading-snug">
                                        {n.subject || n.detail_subject || `Notice #${id}`}
                                      </div>
                                      <span
                                        className="rounded-full px-2 py-0.5 text-[10.5px] font-bold"
                                        style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.color }}
                                      >
                                        {sev}
                                      </span>
                                    </div>
                                    <div className="mt-1 line-clamp-2 text-xs leading-relaxed text-[#93a1b5]">
                                      {noticePreviewText(n)}
                                    </div>
                                    <div className="mt-1 text-[11px] text-[#5b6779]">{formatNoticeDate(n)}</div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </AnimatedCollapse>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3.5 rounded-xl border border-dashed border-[rgba(148,163,184,.25)]">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-[13px] text-[#93a1b5] hover:text-[#e7edf6]"
                    onClick={() => setPasteOpen((v) => !v)}
                  >
                    <span className="text-[11px]">{pasteOpen ? '▲' : '▼'}</span>
                    Can&apos;t find it? Paste the Amazon email manually
                  </button>
                  <AnimatedCollapse open={pasteOpen || (!notifications.length && noticeText)} durationMs={190}>
                    <div className="px-4 pb-4">
                      <textarea
                        disabled={readOnly}
                        placeholder="Paste the full text of the performance notification email here…"
                        value={noticeText}
                        onChange={(e) => setNoticeText(e.target.value)}
                        className="min-h-[120px] w-full resize-y rounded-[10px] border border-[rgba(148,163,184,.2)] bg-[#0a0f16] px-3.5 py-3 text-[13px] text-[#e7edf6] outline-none"
                      />
                    </div>
                  </AnimatedCollapse>
                </div>
              </div>
            )}

            {/* Step 2 — Details */}
            {stepIndex === 2 && (
              <div className={card}>
                <div className="flex flex-wrap items-start gap-3">
                  <div>
                    <h2 className="m-0 text-lg font-semibold">Here&apos;s what we found</h2>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-[#93a1b5]">
                      Extracted from your notice and account. Correct anything that looks off — this drives your POA.
                    </p>
                  </div>
                  <button type="button" className={`${outlineBtn} ml-auto text-[12.5px]`} onClick={runParse} disabled={readOnly || parsing || !noticeText.trim()}>
                    {parsing ? 'Parsing…' : '✦ Re-parse notice'}
                  </button>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-x-[18px] gap-y-3.5 sm:grid-cols-2">
                  {[
                    ['store_name', 'Store name'],
                    ['merchant_token', 'Merchant / Seller ID'],
                    ['marketplace', 'Marketplace'],
                    ['violation_type', 'Violations status'],
                    ['deactivation_date', 'Deactivation date'],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <div className="mb-1.5 flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-[#5b6779]">
                        {label}
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: parsed[key] ? '#34d399' : '#fbbf24' }} />
                      </div>
                      <input
                        className={fieldBox}
                        disabled={readOnly}
                        value={parsed[key] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setParsed((prev) => {
                            if (key === 'deactivation_date') {
                              return { ...prev, deactivation_date: value, notice_date: value };
                            }
                            return { ...prev, [key]: value };
                          });
                        }}
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <div className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-[#5b6779]">Affected ASINs (one per line)</div>
                    <textarea
                      rows={2}
                      disabled={readOnly}
                      className={fieldBox}
                      value={(parsed.affected_asins || []).join('\n')}
                      onChange={(e) => setListField('affected_asins', e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-[#5b6779]">Amazon requested items</div>
                    <textarea
                      rows={3}
                      disabled={readOnly}
                      className={fieldBox}
                      placeholder="Greater detail on the root cause(s)…"
                      value={(parsed.amazon_requested_items || []).join('\n')}
                      onChange={(e) => setListField('amazon_requested_items', e.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-5 rounded-xl border border-[rgba(63,214,204,.2)] bg-[rgba(63,214,204,.06)] px-[18px] py-4">
                  <div className="text-[11.5px] font-bold uppercase tracking-wide text-[#3fd6cc]">Suggested violation family</div>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <select
                      disabled={readOnly}
                      value={family}
                      onChange={(e) => setFamily(e.target.value)}
                      className="min-w-[280px] flex-1 rounded-[9px] border border-[rgba(148,163,184,.2)] bg-[#0d1420] px-3.5 py-2.5 text-[13.5px] font-semibold text-[#e7edf6] outline-none"
                    >
                      {Object.entries(FAMILY_LABELS).map(([id, label]) => (
                        <option key={id} value={id}>Family {id} — {label}</option>
                      ))}
                    </select>
                    <div className="text-xs text-[#93a1b5]">
                      {routingNote || 'Determines which questions Amazon expects you to answer'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 — Answers */}
            {stepIndex === 3 && (
              <div className="mt-6 flex min-h-[calc(100vh-220px)] flex-col">
                {Number(family) === 4 ? (
                  <div className={card}>
                    <h2 className="m-0 text-lg font-semibold">Documents you are providing</h2>
                    <p className="mt-2 text-[13.5px] text-[#93a1b5]">Family 4 — list each document Amazon asked for. No root-cause narrative.</p>
                    <textarea
                      rows={8}
                      disabled={readOnly}
                      className={`${fieldBox} mt-4 min-h-[190px]`}
                      value={steps.supporting_evidence || ''}
                      onChange={(e) => setSteps((prev) => ({ ...prev, supporting_evidence: e.target.value }))}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3.5">
                      <div className="text-xs font-bold tracking-wide text-[#5b6779]">QUESTION {answerQ + 1} OF 3</div>
                      {coachingPrefetching && (
                        <div className="inline-flex items-center gap-1.5 text-[11px] text-[#93a1b5]">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Loading coaching for all questions…
                        </div>
                      )}
                      <div className="h-[3px] flex-1 overflow-hidden rounded-sm bg-[rgba(148,163,184,.12)]">
                        <div
                          className="h-full rounded-sm transition-all"
                          style={{
                            width: `${((answerQ + 1) / 3) * 100}%`,
                            background: 'linear-gradient(90deg,#3fd6cc,#6d7cf5)',
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-3.5 flex min-h-0 flex-1 flex-wrap gap-5 items-stretch">
                      <div className="min-w-[min(330px,100%)] flex-1 rounded-[14px] border border-[rgba(148,163,184,.14)] bg-[#101724] p-7">
                        <h2 className="m-0 text-[19px] font-semibold">{ANSWER_META[answerQ].title}</h2>
                        <p className="mt-2 text-[13.5px] leading-relaxed text-[#93a1b5]">{ANSWER_META[answerQ].sub}</p>
                        <textarea
                          disabled={readOnly}
                          placeholder={ANSWER_META[answerQ].ph}
                          value={steps[ANSWER_META[answerQ].key] || ''}
                          onChange={(e) => setSteps((prev) => ({ ...prev, [ANSWER_META[answerQ].key]: e.target.value }))}
                          className="mt-4 max-h-[min(42vh,320px)] min-h-[160px] w-full resize-y overflow-y-auto rounded-[11px] border border-[rgba(148,163,184,.2)] bg-[#0a0f16] px-4 py-3.5 text-[13.5px] leading-relaxed text-[#e7edf6] outline-none"
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <div className="text-[11.5px] text-[#5b6779]">{wordCount(steps[ANSWER_META[answerQ].key])} words</div>
                          <div
                            className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={{
                              background: ansOk(answerQ) ? 'rgba(52,211,153,.1)' : 'rgba(148,163,184,.08)',
                              color: ansOk(answerQ) ? '#6ee7b7' : '#5b6779',
                            }}
                          >
                            {ansOk(answerQ) ? '✓ Good length' : 'Aim for 40+ words'}
                          </div>
                        </div>
                        <AnimatedCollapse open={!showSug[answerQ]} durationMs={150} className="mt-4">
                          <button
                            type="button"
                            disabled={readOnly || draftLoading}
                            onClick={() => loadDraft(answerQ)}
                            className="flex items-center gap-2 rounded-[10px] border border-[rgba(109,124,245,.45)] bg-[rgba(109,124,245,.12)] px-[18px] py-2.5 text-[13px] font-semibold text-[#b9c2ff] hover:bg-[rgba(109,124,245,.2)] disabled:opacity-50"
                          >
                            {draftLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : '✦'} Draft this answer with AI
                          </button>
                        </AnimatedCollapse>
                        <AnimatedCollapse open={showSug[answerQ]} durationMs={220} className="mt-4">
                          <div className="rounded-xl border border-[rgba(109,124,245,.35)] bg-[rgba(109,124,245,.07)] px-[18px] py-4">
                            <div className="text-[11.5px] font-bold tracking-wide text-[#b9c2ff]">
                              ✦ AI DRAFT <span className="font-normal tracking-normal text-[#8891c9]">— based on your notice + Protego ruleset</span>
                            </div>
                            <div className="mt-2.5 max-h-[160px] overflow-y-auto whitespace-pre-line text-[13px] leading-relaxed text-[#dbe0ff]">
                              {draftLoading ? 'Generating draft…' : (aiDrafts[answerQ] || ANSWER_META[answerQ].sug)}
                            </div>
                            <div className="mt-3.5 flex gap-2.5">
                              <button
                                type="button"
                                className="rounded-lg border-none bg-[#6d7cf5] px-4 py-2 text-[12.5px] font-semibold text-white hover:bg-[#7f8cf7] disabled:opacity-50"
                                disabled={draftLoading || !aiDrafts[answerQ]}
                                onClick={() => {
                                  const draft = aiDrafts[answerQ] || ANSWER_META[answerQ].sug;
                                  setSteps((prev) => ({ ...prev, [ANSWER_META[answerQ].key]: draft }));
                                  setShowSug((s) => { const n = [...s]; n[answerQ] = false; return n; });
                                }}
                              >
                                Use this draft
                              </button>
                              <button
                                type="button"
                                className="rounded-lg border border-[rgba(148,163,184,.25)] bg-transparent px-4 py-2 text-[12.5px] text-[#93a1b5] hover:text-[#e7edf6] disabled:opacity-50"
                                disabled={draftLoading}
                                onClick={() => loadDraft(answerQ, true)}
                              >
                                Regenerate
                              </button>
                            </div>
                          </div>
                        </AnimatedCollapse>
                      </div>
                      <div className="flex w-full max-w-[340px] min-w-[260px] flex-1 flex-col gap-3.5 self-stretch">
                        <div className="flex max-h-[min(70vh,560px)] min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto pr-1">
                          <div className="rounded-xl border border-[rgba(52,211,153,.25)] bg-[rgba(52,211,153,.06)] px-[18px] py-4">
                            <div className="text-[11px] font-bold tracking-wide text-[#6ee7b7]">✓ STRONG ANSWER</div>
                            <div className="mt-2 text-[12.5px] leading-relaxed text-[#c9d5cf]">
                              <CoachPanelBody
                                items={coaching[answerQ].strong}
                                fallback={ANSWER_META[answerQ].good}
                                loading={coaching[answerQ].loading && !coaching[answerQ].loaded}
                              />
                            </div>
                          </div>
                          <div className="rounded-xl border border-[rgba(248,113,113,.22)] bg-[rgba(248,113,113,.05)] px-[18px] py-4">
                            <div className="text-[11px] font-bold tracking-wide text-[#fca5a5]">✕ INVESTIGATORS REJECT</div>
                            <div className="mt-2 text-[12.5px] leading-relaxed text-[#d8cccc]">
                              <CoachPanelBody
                                items={coaching[answerQ].weak}
                                fallback={ANSWER_META[answerQ].weak}
                                loading={coaching[answerQ].loading && !coaching[answerQ].loaded}
                              />
                            </div>
                          </div>
                          <div className="rounded-xl border border-[rgba(148,163,184,.14)] bg-[#101724] px-[18px] py-4">
                            <div className="text-[11px] font-bold tracking-wide text-[#5b6779]">TIPS</div>
                            <div className="mt-2 text-[12.5px] leading-relaxed text-[#93a1b5]">
                              <CoachPanelBody
                                items={coaching[answerQ].tips}
                                fallback={ANSWER_META[answerQ].tips}
                                loading={coaching[answerQ].loading && !coaching[answerQ].loaded}
                              />
                            </div>
                          </div>
                          <div className="overflow-hidden rounded-xl border border-[rgba(109,124,245,.32)] bg-[#101724]">
                            <div className="border-b border-[rgba(148,163,184,.12)] px-4 py-2.5 text-[11px] font-bold tracking-wide text-[#b9c2ff]">✦ ASK PROTEGO AI</div>
                            <div className="flex max-h-[180px] flex-col gap-2 overflow-y-auto px-3.5 py-3">
                              {chat.map((m, idx) => (
                                <div
                                  key={idx}
                                  className="max-w-[92%] rounded-[10px] px-2.5 py-2 text-xs leading-relaxed"
                                  style={{
                                    alignSelf: m.who === 'ai' ? 'flex-start' : 'flex-end',
                                    background: m.who === 'ai' ? 'rgba(109,124,245,.1)' : 'rgba(63,214,204,.1)',
                                    color: m.who === 'ai' ? '#dbe0ff' : '#c9f5f2',
                                  }}
                                >
                                  {m.text}
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2 border-t border-[rgba(148,163,184,.12)] p-2.5">
                              <input
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') chatSend(); }}
                                placeholder="Is my answer specific enough?"
                                className="min-w-0 flex-1 rounded-lg border border-[rgba(148,163,184,.2)] bg-[#0a0f16] px-2.5 py-2 text-xs text-[#e7edf6] outline-none"
                              />
                              <button type="button" onClick={chatSend} disabled={chatLoading} className="rounded-lg border-none bg-[#6d7cf5] px-3 py-2 text-xs font-bold text-white hover:bg-[#7f8cf7] disabled:opacity-50">{chatLoading ? '…' : '→'}</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 4 — Documents */}
            {stepIndex === 4 && (
              <div className={card}>
                <h2 className="m-0 text-lg font-semibold">Supporting documents</h2>
                <p className="mt-2 max-w-[640px] text-[13.5px] leading-relaxed text-[#93a1b5]">
                  {docsLoading ? 'Protego AI is listing documents for this notice…' : 'Attach what you have — the POA will reference each document by name.'}
                </p>
                <div className="mt-5 flex flex-col gap-2.5">
                  {docRequirements.map((d, i) => {
                    const on = attachedDocs[i];
                    const req = d.requirement === 'required';
                    return (
                      <div
                        key={`${d.name}-${i}`}
                        className="flex items-start gap-3.5 rounded-xl px-[18px] py-4"
                        style={{
                          background: on ? 'rgba(52,211,153,.04)' : '#0d1420',
                          border: on ? '1px solid rgba(52,211,153,.3)' : '1px solid rgba(148,163,184,.14)',
                        }}
                      >
                        <div
                          className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[7px] text-xs font-bold"
                          style={{
                            background: on ? '#34d399' : 'transparent',
                            color: '#06121a',
                            border: on ? 'none' : '2px solid #5b6779',
                          }}
                        >
                          {on ? '✓' : ''}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-2 text-[13.5px] font-semibold">
                            {d.name}
                            <span
                              className="rounded-full px-2 py-0.5 text-[10.5px] font-bold"
                              style={{
                                background: req ? 'rgba(251,191,36,.1)' : 'rgba(148,163,184,.08)',
                                color: req ? '#fcd34d' : '#5b6779',
                              }}
                            >
                              {docRequirementLabel(d.requirement)}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-[#93a1b5]">{d.why}</div>
                          {on && (
                            <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-[rgba(52,211,153,.25)] bg-[rgba(52,211,153,.08)] px-2.5 py-1 text-[11.5px] text-[#6ee7b7]">
                              {docFiles[i] || 'Marked attached'}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          disabled={readOnly}
                          onClick={() => {
                            setAttachedDocs((prev) => {
                              const next = [...prev];
                              next[i] = !next[i];
                              return next;
                            });
                            if (!attachedDocs[i]) {
                              setDocFiles((prev) => {
                                const next = [...prev];
                                next[i] = next[i] || `${d.name.toLowerCase().replace(/\s+/g, '-')}.pdf`;
                                return next;
                              });
                            }
                          }}
                          className="flex-none rounded-lg px-4 py-2 text-xs font-semibold"
                          style={{
                            border: on ? '1px solid rgba(148,163,184,.25)' : 'none',
                            background: on ? 'transparent' : '#3fd6cc',
                            color: on ? '#93a1b5' : '#06121a',
                          }}
                        >
                          {on ? 'Remove' : 'Attach'}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 rounded-xl border-[1.5px] border-dashed border-[rgba(148,163,184,.3)] px-6 py-7 text-center text-[13px] text-[#5b6779]">
                  Drop any other files here, or <span className="text-[#3fd6cc]">browse</span> — PDF, JPG, PNG · 25 MB max
                  <div className="mt-3">
                    <textarea
                      rows={2}
                      disabled={readOnly}
                      placeholder="Or list extra evidence filenames / notes…"
                      className={fieldBox}
                      value={steps.supporting_evidence || ''}
                      onChange={(e) => setSteps((prev) => ({ ...prev, supporting_evidence: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5 — Review */}
            {stepIndex === 5 && (
              <div className={card}>
                <h2 className="m-0 text-lg font-semibold">Review everything</h2>
                <p className="mt-2 text-[13.5px] leading-relaxed text-[#93a1b5]">One last look before we generate. You can still edit any section.</p>
                {reviewLoading && (
                  <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#93a1b5]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking answers against Protego rules…
                  </div>
                )}
                <div className="mt-5 flex flex-col gap-2.5">
                  {!reviewLoaded ? (
                    REVIEW_SECTION_LABELS.map((label) => (
                      <ReviewSkeletonRow key={label} label={label} />
                    ))
                  ) : (
                  reviewRows.map((r) => (
                    <div key={r.label} className="flex items-start gap-3.5 rounded-xl border border-[rgba(148,163,184,.14)] bg-[#0d1420] px-[18px] py-4">
                      <div
                        className="mt-0.5 flex-none rounded-full px-2.5 py-0.5 text-[11px] font-bold"
                        style={{
                          background: r.status === 'ready' ? 'rgba(52,211,153,.1)' : 'rgba(251,191,36,.1)',
                          border: r.status === 'ready' ? '1px solid rgba(52,211,153,.3)' : '1px solid rgba(251,191,36,.3)',
                          color: r.status === 'ready' ? '#6ee7b7' : '#fcd34d',
                        }}
                      >
                        {r.status === 'ready' ? 'Ready' : 'Needs work'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13.5px] font-semibold">{r.label}</div>
                        <div className="mt-1 line-clamp-2 text-[12.5px] leading-relaxed text-[#93a1b5]">{r.summary}</div>
                        {Array.isArray(r.reasons) && r.reasons.length > 0 && (
                          <ul className="mt-2 list-disc pl-4 text-[11.5px] leading-relaxed text-[#fcd34d]">
                            {r.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                          </ul>
                        )}
                      </div>
                      <button type="button" className={outlineBtn} onClick={r.go}>Edit</button>
                    </div>
                  ))
                  )}
                </div>
              </div>
            )}

            {/* Step 6 — Ready / submitted */}
            {stepIndex === 6 && (
              <div className="mt-6">
                <div className="flex flex-wrap items-center gap-3 rounded-[13px] border border-[rgba(52,211,153,.3)] bg-[rgba(52,211,153,.08)] px-5 py-4">
                  <div className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[rgba(52,211,153,.15)] text-base font-bold text-[#34d399]">✓</div>
                  <div>
                    <div className="text-[15px] font-semibold">Your Plan of Action is ready</div>
                    <div className="mt-0.5 text-[12.5px] text-[#93a1b5]">
                      {generatingPoa
                        ? 'Protego AI is polishing your answers into an investigator-ready POA…'
                        : generatedPoa?.source === 'claude'
                          ? 'Revamped by Protego AI — download the PDF and paste into Seller Central → Account Health.'
                          : 'Showing your intake text. Use Revamp again if AI timed out or failed.'}
                    </div>
                  </div>
                  <div className="ml-auto flex flex-wrap gap-2.5">
                    {allowResubmit && (
                      <button
                        type="button"
                        className={primaryBtn}
                        onClick={() => setStepIndex(0)}
                      >
                        Edit & submit again
                      </button>
                    )}
                    <button
                      type="button"
                      className={outlineBtn}
                      onClick={revampPoaAgain}
                      disabled={generatingPoa}
                    >
                      {generatingPoa ? 'Revamping…' : '✦ Revamp again'}
                    </button>
                    <button
                      type="button"
                      className={primaryBtn}
                      style={{ background: 'linear-gradient(135deg,#3fd6cc,#6d7cf5)' }}
                      onClick={downloadPoaPdf}
                      disabled={generatingPoa}
                    >
                      {generatingPoa ? 'Preparing…' : 'Download PDF'}
                    </button>
                  </div>
                </div>
                <div className="mt-4 rounded-[14px] bg-[#f6f5f1] px-8 py-10 text-[#1c2430] shadow-[0_20px_50px_rgba(0,0,0,.4)] md:px-[52px]">
                  {generatingPoa && !generatedPoa ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-[#5b6779]">
                      <Loader2 className="h-8 w-8 animate-spin text-[#3fd6cc]" />
                      <div className="text-[14px] font-medium">Claude is revamping your POA…</div>
                      <div className="max-w-sm text-center text-[12.5px] text-[#8a8f98]">
                        Turning your intake into a polished, investigator-ready Plan of Action.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-[11px] font-bold tracking-[1.5px] text-[#8a8f98]">
                        PLAN OF ACTION — {(parsed.store_name || 'STORE').toUpperCase()} · {parsed.merchant_token || '—'} · {(parsed.marketplace || 'AMAZON').toUpperCase()}
                      </div>
                      {generatedPoa?.source === 'claude' && (
                        <div className="mt-2 inline-flex rounded-full bg-[rgba(63,214,204,.15)] px-2.5 py-0.5 text-[10px] font-bold tracking-wide text-[#0d9488]">
                          ✦ REVAMPED BY PROTEGO AI
                        </div>
                      )}
                      <h2 className="mt-4 text-[21px] font-bold text-[#131a24]">
                        {generatedPoa?.title || `Plan of Action: ${parsed.violation_type || FAMILY_LABELS[family] || 'Account reinstatement'}`}
                      </h2>
                      {generatedPoa?.opening ? (
                        <p className="mt-5 text-[13.5px] leading-relaxed text-[#39424e] whitespace-pre-wrap">{generatedPoa.opening}</p>
                      ) : null}
                      {(generatedPoa?.sections?.length
                        ? generatedPoa.sections
                        : [
                          ['ROOT CAUSES OF THE VIOLATION:', steps.root_cause],
                          ['ACTIONS TAKEN TO RESOLVE THE ISSUES:', steps.corrective_actions],
                          ['STEPS TAKEN TO PREVENT VIOLATIONS IN THE FUTURE:', steps.preventative_measures],
                          ['SUPPORTING DOCUMENTS:', steps.supporting_evidence],
                        ].map(([h, body]) => ({ heading: h, body }))
                      ).map((section) => (
                        <div key={section.heading}>
                          <div className="mt-5 text-[13px] font-bold tracking-wide text-[#131a24]">{section.heading}</div>
                          <p className="mt-2 text-[13.5px] leading-relaxed text-[#39424e] whitespace-pre-wrap">{section.body || '—'}</p>
                        </div>
                      ))}
                      {generatedPoa?.closing ? (
                        <p className="mt-6 text-[13.5px] leading-relaxed text-[#39424e] whitespace-pre-wrap">{generatedPoa.closing}</p>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Nav — sticky so Next/Back / Generate stay visible without full-page scroll */}
            {stepIndex < 5 && (
              <div className="sticky bottom-0 z-30 mt-6 -mx-1 border-t border-[rgba(148,163,184,.12)] bg-[rgba(10,15,22,.94)] px-1 py-3 backdrop-blur-md supports-[backdrop-filter]:bg-[rgba(10,15,22,.82)]">
                <div className="flex items-center">
                  {(stepIndex > 0 || answerQ > 0) && (
                    <button type="button" className={outlineBtn} onClick={goBack}>← Back</button>
                  )}
                  <button
                    type="button"
                    className={`${primaryBtn} ml-auto`}
                    onClick={goNext}
                    disabled={!readOnly && (!canAdvance || parsing || saving || Boolean(busyOverlay))}
                  >
                    {nextLabel}
                  </button>
                </div>
              </div>
            )}
            {stepIndex === 5 && (
              <div className="sticky bottom-0 z-30 mt-6 -mx-1 border-t border-[rgba(148,163,184,.12)] bg-[rgba(10,15,22,.94)] px-1 py-3 backdrop-blur-md supports-[backdrop-filter]:bg-[rgba(10,15,22,.82)]">
                <div
                  className="flex flex-wrap items-center gap-4 rounded-[13px] border border-[rgba(63,214,204,.3)] px-[22px] py-4"
                  style={{ background: 'linear-gradient(135deg,rgba(63,214,204,.12),rgba(109,124,245,.12))' }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold">
                      {canSubmitPoa
                        ? 'Ready to generate your Plan of Action'
                        : 'Add at least one answer before generating'}
                    </div>
                    <div className="mt-1 text-[12.5px] text-[#93a1b5]">
                      {reviewLoaded
                        ? `Validated against Protego POA Ruleset v1 for Family ${family} violations.`
                        : reviewLoading
                          ? 'Checking answers against Protego rules…'
                          : 'Scroll the review above — this bar stays fixed so you can generate anytime.'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={primaryBtn}
                    style={{ background: 'linear-gradient(135deg,#3fd6cc,#6d7cf5)' }}
                    onClick={handleSubmit}
                    disabled={saving || !canSubmitPoa || Boolean(busyOverlay)}
                  >
                    {saving ? 'Submitting…' : '✦ Generate Plan of Action'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
